import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { Checkbox, Spin } from "antd";
import Box from "@app/components/box/box";
import profile from "@app/state/store/profile";
import "./style/settings.scoped.scss";

const PREFERENCE_CATEGORIES = [
    "literatureSearch",
    "reportDocumentAssigned",
    "comments",
    "articleRequest",
];

const Settings = observer(({ className, ...props }) => {
    const [preferences, setPreferences] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadPreferences();
    }, []);

    const loadPreferences = async () => {
        setLoading(true);
        const prefs = await profile.getNotificationPreferences();
        if (prefs) {
            setPreferences(prefs);
        }
        setLoading(false);
    };

    // Check if all categories have a specific channel enabled
    const isAllChecked = (channel) => {
        if (!preferences) {
            return false;
        }
        return PREFERENCE_CATEGORIES.every((cat) => preferences[cat]?.[channel] !== false);
    };

    // Handle "All" checkbox - toggles all categories for a channel
    const handleAllChange = async (channel, checked) => {
        const updatedPreferences = { ...preferences };
        PREFERENCE_CATEGORIES.forEach((cat) => {
            updatedPreferences[cat] = {
                ...updatedPreferences[cat],
                [channel]: checked,
            };
        });

        setPreferences(updatedPreferences);
        await profile.updateNotificationPreferences(updatedPreferences);
    };

    // Handle individual category checkbox
    const handleChange = async (category, channel, checked) => {
        const updatedPreferences = {
            ...preferences,
            [category]: {
                ...preferences[category],
                [channel]: checked,
            },
        };

        setPreferences(updatedPreferences);
        await profile.updateNotificationPreferences(updatedPreferences);
    };

    if (loading) {
        return (
            <Box title="Settings" className={`settings ${className}`} {...props}>
                <div className="loading-container">
                    <Spin size="large" />
                </div>
            </Box>
        );
    }

    if (!preferences) {
        return (
            <Box title="Settings" className={`settings ${className}`} {...props}>
                <div className="error-message">Failed to load notification preferences</div>
            </Box>
        );
    }

    const notificationTypes = [
        {
            key: "literatureSearch",
            title: "Literature Search",
            description: "Alerts on search Auto update results",
        },
        {
            key: "reportDocumentAssigned",
            title: "Report Document assigned",
            description: "Notifications on assigned report document",
        },
        {
            key: "comments",
            title: "Comments",
            description: "Comments on your posts and replies to comments",
        },
        {
            key: "articleRequest",
            title: "Article Requests",
            description: "Notifications when your Article Galaxy requests become available",
        },
    ];

    return (
        <Box title="Settings" className={`settings ${className}`} {...props}>
            <div className="notification-preferences">
                <h3 className="section-title">Notification Preferences</h3>
                <p className="section-description">Manage all your CAPTIS notifications</p>

                <div className="preferences-table">
                    <div className="table-header">
                        <div className="column-type"></div>
                        <div className="column-channel">Email</div>
                        <div className="column-channel">In-app</div>
                    </div>

                    {/* All row - UI only toggle */}
                    <div className="preference-row">
                        <div className="column-type">
                            <div className="type-title">All</div>
                            <div className="type-description">Allow all Notifications</div>
                        </div>
                        <div className="column-channel">
                            <Checkbox
                                checked={isAllChecked("email")}
                                onChange={(e) => handleAllChange("email", e.target.checked)}
                                disabled={profile.saving}
                            />
                        </div>
                        <div className="column-channel">
                            <Checkbox
                                checked={isAllChecked("inApp")}
                                onChange={(e) => handleAllChange("inApp", e.target.checked)}
                                disabled={profile.saving}
                            />
                        </div>
                    </div>

                    {notificationTypes.map((type) => (
                        <div key={type.key} className="preference-row">
                            <div className="column-type">
                                <div className="type-title">{type.title}</div>
                                <div className="type-description">{type.description}</div>
                            </div>
                            <div className="column-channel">
                                <Checkbox
                                    checked={preferences[type.key]?.email}
                                    onChange={(e) =>
                                        handleChange(type.key, "email", e.target.checked)
                                    }
                                    disabled={profile.saving}
                                />
                            </div>
                            <div className="column-channel">
                                <Checkbox
                                    checked={preferences[type.key]?.inApp}
                                    onChange={(e) =>
                                        handleChange(type.key, "inApp", e.target.checked)
                                    }
                                    disabled={profile.saving}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </Box>
    );
});

export default Settings;
